/**
 * ScreenFlowBuilder — builder for Screen Flow section.
 *
 * Thin wrapper around shared flow components, configured for screen_flow source.
 * Same progressive depth loading and cross-session continuation as EventFlowBuilder.
 */
import { useCallback, useState, useMemo, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { BuilderShell } from '@/components/builder/BuilderShell';
import { api } from '@/services/api';
import { endpoints } from '@/services/endpoints';
import { useSavedReport } from '@/hooks/useSavedReport';
import { useSectionCache } from '@/hooks/useSectionCache';
import { useFlowState } from '@/sections/flow/useFlowState';
import { FlowConfigPanel } from '@/sections/flow/FlowConfigPanel';
import { FlowResultsPanel } from '@/sections/flow/FlowResultsPanel';
import { FlowGraphBuilder, FlowTreeManager } from '@/sections/flow/FlowGraphEngine';
import type { FlowState, FlowSectionConfig, FlowRecord, FlowLevel, FlowDataResponse } from '@/sections/flow/flow.types';

const CHUNK_SIZE = 10;

const SECTION_CONFIG: FlowSectionConfig = {
  source: 'screen_flow',
  sectionId: 'screen_flow',
  sectionRoute: '/screen-flow',
  accentColor: '#10B981',
  title: 'Screen Flow',
  subtitle: 'Track screen-to-screen navigation patterns',
  itemLabel: 'Screen',
  itemsLabel: 'Screens',
};

interface CachedFlowData {
  records: FlowRecord[];
  levels: FlowLevel[];
  loadedDepth: number;
  maxAvailableDepth: number;
}

export function ScreenFlowBuilder() {
  const { reportId } = useParams();
  const { state, update, reset, canRun, toFlowPayload, toContinuationPayload, dates, searchParams } = useFlowState('screen_flow');

  const [levels, setLevels] = useState<FlowLevel[]>([]);
  const [records, setRecords] = useState<FlowRecord[] | null>(null);

  const [loadedDepth, setLoadedDepth] = useState(0);
  const [maxAvailableDepth, setMaxAvailableDepth] = useState(0);
  const prefetchAbortRef = useRef<AbortController | null>(null);
  const prefetchingRef = useRef(false);

  const [continuationLoadingLevels, setContinuationLoadingLevels] = useState<Set<number>>(new Set());
  const [continuationRecordsMap, setContinuationRecordsMap] = useState<Map<number, FlowRecord[]>>(new Map());
  const graphBuilderRef = useRef<FlowGraphBuilder | null>(null);

  const levelsRef = useRef<FlowLevel[]>([]);
  levelsRef.current = levels;

  const { savedReport } = useSavedReport<FlowState>({
    reportId,
    sectionId: SECTION_CONFIG.sectionId,
    searchParams,
    update,
    mapConfig: useCallback((config: Record<string, unknown>) => {
      return {
        preset: (config.preset as string) || (config.datePreset as string) || '',
        start: (config.start as string) || (config.startDate as string) || '',
        end: (config.end as string) || (config.endDate as string) || '',
        filterMode: ((config.filterMode as string) || 'exclude') as 'include' | 'exclude',
        events: Array.isArray(config.events)
          ? config.events as string[]
          : typeof config.events === 'string' && config.events
            ? (config.events as string).split(',').filter(Boolean)
            : Array.isArray(config.excludedEvents)
              ? config.excludedEvents as string[]
              : [],
        startFrom: (config.startFrom as string) || (config.startFromEvent as string) || '',
        collapse: Boolean(config.collapse ?? config.collapseRepeats),
        kpi: (config.kpi as string) || (config.kpiEvent as string) || '',
        filters: typeof config.filters === 'string'
          ? config.filters
          : '',
      };
    }, []),
  });

  const [cachedData, setCachedData] = useSectionCache<CachedFlowData>('screen-flow-builder');

  useMemo(() => {
    if (cachedData && !records) {
      setRecords(cachedData.records);
      setLevels(cachedData.levels);
      setLoadedDepth(cachedData.loadedDepth);
      setMaxAvailableDepth(cachedData.maxAvailableDepth);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const prefetchNext = useCallback(async (
    currentDepth: number,
    maxDepth: number,
    payloadBuilder: typeof toFlowPayload,
    initialRecords: FlowRecord[],
  ) => {
    if (prefetchingRef.current) return;
    if (currentDepth >= maxDepth) return;

    prefetchingRef.current = true;

    let depth = currentDepth;
    let accumulatedRecords = [...initialRecords];
    while (depth < maxDepth) {
      const depthFrom = depth;
      const depthTo = Math.min(depth + CHUNK_SIZE, maxDepth);

      try {
        const payload = payloadBuilder(depthFrom, depthTo);
        const { data: chunk, meta } = await api.postWithMeta<FlowDataResponse>(
          endpoints.flow.data(),
          payload,
        );

        const newRecords = chunk.flow;
        if (newRecords.length === 0) break;

        accumulatedRecords = [...accumulatedRecords, ...newRecords];
        setRecords([...accumulatedRecords]);
        depth = depthTo;
        setLoadedDepth(depthTo);

        const serverMax = (meta?.maxAvailableDepth as number) ?? maxDepth;
        setMaxAvailableDepth(serverMax);

        const hasMore = (meta?.hasMore as boolean) ?? depthTo < maxDepth;
        if (!hasMore) break;
      } catch {
        break;
      }
    }

    prefetchingRef.current = false;
  }, []);

  useEffect(() => {
    return () => {
      prefetchAbortRef.current?.abort();
    };
  }, []);

  const queryMutation = useMutation({
    mutationFn: async () => {
      prefetchAbortRef.current?.abort();
      prefetchingRef.current = false;

      const payload = toFlowPayload(undefined, CHUNK_SIZE);
      return api.postWithMeta<FlowDataResponse>(endpoints.flow.data(), payload);
    },
    onSuccess: ({ data: response, meta }) => {
      const flowRecords = response.flow;
      setRecords(flowRecords);

      const builder = new FlowGraphBuilder(flowRecords);
      graphBuilderRef.current = builder;
      const manager = new FlowTreeManager(builder);
      manager.initialize();
      setLevels(manager.levels);

      const initialDepth = CHUNK_SIZE;
      const serverMax = (meta?.maxAvailableDepth as number) ?? CHUNK_SIZE;
      setLoadedDepth(initialDepth);
      setMaxAvailableDepth(serverMax);

      setCachedData({
        records: flowRecords,
        levels: manager.levels,
        loadedDepth: initialDepth,
        maxAvailableDepth: serverMax,
      });

      const hasMore = (meta?.hasMore as boolean) ?? false;
      if (hasMore) {
        prefetchNext(initialDepth, serverMax, toFlowPayload, flowRecords);
      }
    },
  });

  const handleRun = useCallback(() => {
    if (canRun) queryMutation.mutate();
  }, [canRun, queryMutation]);

  const handleLevelsChange = useCallback(
    (newLevels: FlowLevel[]) => {
      setLevels(newLevels);
      if (records) {
        setCachedData({
          records,
          levels: newLevels,
          loadedDepth,
          maxAvailableDepth,
        });
      }
    },
    [records, setCachedData, loadedDepth, maxAvailableDepth],
  );

  const buildSessionPaths = useCallback((currentLevels: FlowLevel[], clickedLevel: number): string[][] => {
    const sessionPaths: string[][] = [];
    let currentPath: string[] = [];
    let currentSession = 0;

    for (let i = 0; i < clickedLevel; i++) {
      const lev = currentLevels[i];
      const levSession = lev.sessionIndex ?? 0;

      if (levSession !== currentSession && currentPath.length > 0) {
        sessionPaths.push([...currentPath]);
        currentPath = [];
        currentSession = levSession;
      }

      if (lev.selected && lev.selected !== '(none)') {
        currentPath.push(lev.selected);
      }
    }

    if (currentPath.length > 0) {
      sessionPaths.push(currentPath);
    }

    return sessionPaths;
  }, []);

  const handleContinuationRequest = useCallback(
    async (level: number, currentSessionIndex: number) => {
      if (!records) return;

      const nextSessionIndex = currentSessionIndex + 1;

      setContinuationLoadingLevels(prev => new Set(prev).add(level));

      try {
        const sessionPaths = buildSessionPaths(levels, level);

        if (sessionPaths.length === 0) return;

        const payload = toContinuationPayload(sessionPaths, CHUNK_SIZE);
        const { data: response } = await api.postWithMeta<FlowDataResponse>(
          endpoints.flow.continuation(),
          payload,
        );

        const continuationRecords = response.flow;
        if (continuationRecords.length === 0) {
          const treeManager = new FlowTreeManager(new FlowGraphBuilder(records));
          treeManager.levels = structuredClone(levels);
          treeManager.selectTerminal(level, '(none)');
          setLevels(treeManager.levels);
          return;
        }

        const newMap = new Map(continuationRecordsMap);
        newMap.set(nextSessionIndex, continuationRecords);
        setContinuationRecordsMap(newMap);

        const builder = new FlowGraphBuilder(records);
        graphBuilderRef.current = builder;
        for (const [si, cr] of newMap) {
          builder.mergeContinuation(cr, si);
        }

        const treeManager = new FlowTreeManager(builder);
        treeManager.levels = structuredClone(levels);
        treeManager.expandContinuation(level, nextSessionIndex);

        setLevels(treeManager.levels);

        if (records) {
          setCachedData({
            records,
            levels: treeManager.levels,
            loadedDepth,
            maxAvailableDepth,
          });
        }
      } catch (err) {
        console.error('[ScreenFlow] Continuation request failed:', err);
        const treeManager = new FlowTreeManager(new FlowGraphBuilder(records));
        treeManager.levels = structuredClone(levels);
        treeManager.selectTerminal(level, '(none)');
        setLevels(treeManager.levels);
      } finally {
        setContinuationLoadingLevels(prev => {
          const next = new Set(prev);
          next.delete(level);
          return next;
        });
      }
    },
    [records, levels, buildSessionPaths, toContinuationPayload, setCachedData, loadedDepth, maxAvailableDepth, continuationRecordsMap],
  );

  const hasRun = queryMutation.isSuccess || queryMutation.isError || !!records;

  return (
    <BuilderShell
      title="New Report"
      subtitle={SECTION_CONFIG.title}
      sectionId={SECTION_CONFIG.sectionId}
      sectionRoute={SECTION_CONFIG.sectionRoute}
      reportId={reportId}
      savedReport={savedReport as { report?: { name?: string; description?: string } } | undefined}
      onReset={() => {
        reset();
        prefetchAbortRef.current?.abort();
        prefetchingRef.current = false;
        setRecords(null);
        setLevels([]);
        setLoadedDepth(0);
        setMaxAvailableDepth(0);
        setContinuationLoadingLevels(new Set());
        setContinuationRecordsMap(new Map());
        graphBuilderRef.current = null;
      }}
      onRun={handleRun}
      isRunning={queryMutation.isPending}
      canRun={canRun}
      configForSave={state as unknown as Record<string, unknown>}
      configPanel={
        <FlowConfigPanel
          state={state}
          onChange={update}
          dates={dates}
          config={SECTION_CONFIG}
        />
      }
    >
      <FlowResultsPanel
        isLoading={queryMutation.isPending}
        isError={queryMutation.isError}
        error={queryMutation.error}
        records={records}
        hasRun={hasRun}
        canRun={canRun}
        onRun={handleRun}
        levels={levels}
        onLevelsChange={handleLevelsChange}
        kpiEvent={state.kpi || undefined}
        accentColor={SECTION_CONFIG.accentColor}
        onContinuationRequest={handleContinuationRequest}
        continuationLoadingLevels={continuationLoadingLevels}
        continuationRecordsMap={continuationRecordsMap}
      />
    </BuilderShell>
  );
}
